import {
  useAppStorage,
  useProjectsStorage,
  useWorkspaceStorage,
} from '@pn/core/storage';
import { log } from '@pn/core/utils/debug';
import { usePrevious } from '@pn/core/utils/hooks';
import { arraysShareSameValues } from '@pn/core/utils/logic';
import { isNil } from 'lodash-es';
import React from 'react';
import { clearWorkspaceItem } from '../clearWorkspaceItem';
import { useVisualizeWorkspaceItem } from './useVisualizeWorkspaceItem';

type Props = {
  tableFields: Record<string, string[] | undefined>;
  tableOnly?: boolean;
};

export function useAutoVisualizeWorkspaceItems({
  tableFields,
  tableOnly = false,
}: Props) {
  const { unitSystem } = useAppStorage();
  const { workspaceItems } = useWorkspaceStorage();
  const { isProjectLoaded } = useProjectsStorage();

  const previousWorkspaceItems = usePrevious(workspaceItems, {});

  const visualizeWorkspaceItem = useVisualizeWorkspaceItem({
    tableFields,
    tableOnly,
  });

  React.useEffect(() => {
    const items = Object.values(workspaceItems);

    items.forEach((item) => {
      const haveUnitsChanged =
        !isNil(item.query.unitSystem) && item.query.unitSystem !== unitSystem;

      const haveFieldsChanged = !arraysShareSameValues(
        tableFields[item.dataType] ?? [],
        item.query.fields
      );

      if (
        item.isVisible &&
        (!item.isProcessed || haveUnitsChanged || haveFieldsChanged)
      ) {
        log().info(`auto-visualize workspace item [${item.id} - ${item.name}]`);
        visualizeWorkspaceItem({
          item,
          autoTrigger: true,
          silent: !isProjectLoaded,
        });
      } else if (!item.isVisible && item.isRendered) {
        log().info(`auto-hide workspace item [${item.id} - ${item.name}]`);
        clearWorkspaceItem(item, tableOnly);
      }
    });

    Object.values(previousWorkspaceItems).forEach((prevItem) => {
      if (isNil(workspaceItems[prevItem.id])) {
        clearWorkspaceItem(prevItem, tableOnly);
      }
    });
  }, [
    tableOnly,
    workspaceItems,
    previousWorkspaceItems,
    unitSystem,
    tableFields,
    isProjectLoaded,
    visualizeWorkspaceItem,
  ]);
}
